import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { resetCheckoutGql } from '../screens/checkout/gql';

export const KIOSK_SESSION_KEY = 'entry-ecommerce:kiosk-mode';
const IDLE_TIMEOUT_MS = 60 * 1000;
const WARNING_DURATION_S = 30;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'];

function isKioskSession() {
  try {
    return window.sessionStorage.getItem(KIOSK_SESSION_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

export async function exitKioskMode(slug, resetCheckout) {
  if (resetCheckout) {
    await resetCheckout({ variables: { slug, input: {} } }).catch(() => {});
  }
  try {
    window.sessionStorage.removeItem(KIOSK_SESSION_KEY);
  } catch (error) {
    // Ignore storage failures.
  }
  window.location.href = `/${slug}/kiosk`;
}

export default function useKioskIdleReset(slug) {
  const [resetCheckout] = useMutation(resetCheckoutGql, {
    variables: { slug },
  });
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_DURATION_S);
  const exitStartedRef = useRef(false);

  // Watch for inactivity while the warning isn't showing; once idle too long, open it.
  useEffect(() => {
    if (!slug || warningOpen || !isKioskSession()) return undefined;

    let timer;
    const restartTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setWarningOpen(true), IDLE_TIMEOUT_MS);
    };

    restartTimer();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, restartTimer);
    });

    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, restartTimer);
      });
    };
  }, [slug, warningOpen]);

  // Count down while the warning is open; reset for real if nobody answers in time.
  useEffect(() => {
    if (!warningOpen) return undefined;

    exitStartedRef.current = false;
    setSecondsLeft(WARNING_DURATION_S);
    let seconds = WARNING_DURATION_S;
    const interval = setInterval(() => {
      seconds -= 1;
      if (seconds <= 0) {
        clearInterval(interval);
        setSecondsLeft(0);
        if (!exitStartedRef.current) {
          exitStartedRef.current = true;
          exitKioskMode(slug, resetCheckout);
        }
        return;
      }
      setSecondsLeft(seconds);
    }, 1000);

    return () => clearInterval(interval);
  }, [warningOpen, slug, resetCheckout]);

  return {
    warningOpen,
    secondsLeft,
    continueSession: () => setWarningOpen(false),
    resetNow: () => {
      if (exitStartedRef.current) return;
      exitStartedRef.current = true;
      exitKioskMode(slug, resetCheckout);
    },
  };
}
